// Notes, the player-to-developer loop (wave 13, pillar 1; community routes/notes.ts, docs/build/loop.md). On a game's
// page: the Media tab (screenshots and clips players shared) and the Notes tab ("Changed because of players' notes",
// and the card to leave a note). On the web a session is "Play in browser": the game opens in a new tab, and when the
// player comes back after a minute or more, the card opens once for that session. Notes are not reviews: no stars,
// and nobody but the creator reads a player's words.

import { accessToken, api, API_BASE, ApiError } from "./api.ts";
import type { Game } from "./catalogue.ts";
import { clipPath, fmtAgo, safeMediaUrl, type Clip } from "./clips.ts";
import { clear, errorBox, h, link } from "./dom.ts";
import { currentMe, signedIn } from "./session.ts";
import { reportWebSession, webSession, webSessionId, type PlaySession } from "./playtime.ts";
import { nameSpan } from "./pages/game.ts";

export const FEELS = ["love", "good", "meh", "rough"] as const;
export const CHIPS = ["sound", "controls", "lag", "looks", "difficulty", "bug"] as const;
export type Feel = (typeof FEELS)[number];
export type Chip = (typeof CHIPS)[number];

export const FEEL_LABEL: Record<Feel, string> = { love: "Loved it", good: "Liked it", meh: "Mixed", rough: "Rough" };
export const CHIP_LABEL: Record<Chip | "general", string> = {
  sound: "Sound",
  controls: "Controls",
  lag: "Lag",
  looks: "Looks",
  difficulty: "Difficulty",
  bug: "A bug",
  general: "General",
};
/** Whether the game's developer has claimed its page (and so reads its notes); unclaimed, notes wait for the claim. */
export const claimed = (g: Pick<Game, "creator">) => !!g.creator.claimedBy;

/** Who reads a note, said plainly: the developer, or nobody yet (it waits for them to claim the page). */
export function readerLine(g: Pick<Game, "creator">): string {
  return claimed(g) ? "Only the developer sees what you write." : "Only the developer will see it, once they claim this page.";
}

/** Shown under the text box: the tone this place asks for. */
export const NOTE_HINT = "Short and kind helps most: what you loved, and the one thing that got in the way.";
export const NOTE_MAX = 500;
/** A web session shorter than this doesn't open the card (a misclick, a quick look). */
export const MIN_SESSION_SECONDS = 60;

export interface Shot {
  id: string;
  game_id: string;
  uploader_id: string;
  note_id: string | null;
  version: string;
  visibility: "private" | "public";
  status: string;
  width: number | null;
  height: number | null;
  created_at: number;
  shared_at: number | null;
  file_url: string | null;
}

export interface Note {
  id: string;
  game_id: string;
  author_id: string;
  feel: Feel;
  chips: Chip[];
  body: string;
  version: string;
  session_seconds: number;
  platform: string;
  os: string;
  gpu: string;
  clip_id: string | null;
  credit_ok: boolean;
  status: "open" | "addressed" | "wontfix";
  addressed_in: string | null;
  change: string | null;
  shipped_at: number | null;
  moderation?: "hidden" | "removed";
  created_at: number;
  media?: Shot[];
  same_as?: string | null;
  me_too?: number;
}

export interface ChangeVersion {
  version: string;
  live_at: number;
  notes: number;
  changes: { change: string; notes: number }[];
  chips: Record<string, number>;
  thanks: string[];
}

export interface Changes {
  game_id: string;
  open_notes: number;
  versions: ChangeVersion[];
}

/** "2 sound, 1 controls" for a version's chips, largest first. */
export function chipLine(chips: Record<string, number>): string {
  return Object.entries(chips)
    .sort((a, b) => b[1] - a[1] || CHIPS.indexOf(a[0] as Chip) - CHIPS.indexOf(b[0] as Chip))
    .map(([c, n]) => `${n} ${(CHIP_LABEL[c as Chip] ?? c).toLowerCase()}`)
    .join(", ");
}

/** "3 players' notes" / "1 player's note". */
export const notesWord = (n: number) => (n === 1 ? "1 player's note" : `${n} players' notes`);

/** "Answered 2 notes about controls", "Answered 3 notes: 2 about sound, 1 about looks". */
export function answeredLine(v: Pick<ChangeVersion, "notes" | "chips">): string {
  const n = `${v.notes} ${v.notes === 1 ? "note" : "notes"}`;
  const chips = Object.entries(v.chips).sort((a, b) => b[1] - a[1]);
  if (!chips.length) return `Answered ${n}`;
  const about = (c: string) => (CHIP_LABEL[c as Chip] ?? c).toLowerCase();
  if (chips.length === 1) return `Answered ${n} about ${about(chips[0]![0])}`;
  return `Answered ${n}: ${chips.map(([c, k]) => `${k} about ${about(c)}`).join(", ")}`;
}

/** A version label as players read it: "1.3" → "v1.3", "v1.3" stays, "Halloween" stays. */
export const vLabel = (v: string) => (/^\d/.test(v) ? `v${v}` : v);

/** The body the card posts (community POST /v1/games/:game/notes). */
export function noteBody(o: { feel: Feel; chips: Iterable<Chip>; text: string; version: string; sessionId: string; seconds: number; shotIds: string[]; credit: boolean }) {
  const chips = CHIPS.filter((c) => [...o.chips].includes(c));
  return {
    feel: o.feel,
    chips,
    body: o.text.trim().slice(0, NOTE_MAX),
    version: o.version || "live",
    session_id: o.sessionId,
    session_seconds: Math.max(0, Math.round(o.seconds)),
    platform: "web",
    os: platformName(),
    gpu: "",
    ...(o.shotIds.length ? { shot_ids: o.shotIds } : {}),
    ...(o.credit ? { credit_ok: true } : {}),
  };
}

function platformName(): string {
  try {
    const ua = navigator.userAgent;
    const os = /Mac OS X/.test(ua) ? "macOS" : /Windows/.test(ua) ? "Windows" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Linux/.test(ua) ? "Linux" : "";
    const browser = /Firefox\//.test(ua) ? "Firefox" : /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "";
    return [os, browser].filter(Boolean).join(", ");
  } catch {
    return "";
  }
}

const noted = (session: string) => {
  try {
    return sessionStorage.getItem(`thrdm.noted.${session}`) === "1";
  } catch {
    return false;
  }
};
const markNoted = (session: string) => {
  try {
    sessionStorage.setItem(`thrdm.noted.${session}`, "1");
  } catch {
    /* private mode: the server's one-per-session rule still holds */
  }
};

// ---- The card ------------------------------------------------------------------------

export interface CardSession {
  id: string;
  seconds: number;
}

/**
 * The note card: one tap for how it felt, chips, an optional line, an optional screenshot. `onDone` runs after a
 * send or a skip. Skippable, and never shown twice for one session (sessionStorage, and community's 409).
 */
export function noteCard(g: Game, session: CardSession, onDone: (sent: boolean) => void = () => {}): HTMLElement {
  let feel: Feel | null = null;
  const chips = new Set<Chip>();
  const msg = h("div", { role: "status", class: "small" });
  const feelRow = h("div", { class: "nt-feel", role: "radiogroup", "aria-label": "How did it feel?" });
  const chipRow = h("div", { class: "nt-chips", role: "group", "aria-label": "What was it about? (optional)" });
  const send = h("button", { type: "submit", class: "btn small", disabled: true }, "Send note");
  // the buttons are built once and only their state changes, so keyboard focus stays where it is
  const feelBtns = FEELS.map((f) =>
    h("button", { type: "button", role: "radio", class: "nt-feel-btn", "aria-checked": "false", tabindex: f === FEELS[0] ? "0" : "-1" }, h("span", { class: `nt-dot nt-dot-${f}`, "aria-hidden": "true" }), FEEL_LABEL[f]),
  );
  const pickFeel = (i: number, focus = false) => {
    feel = FEELS[i]!;
    send.disabled = false;
    feelBtns.forEach((b, j) => {
      b.classList.toggle("on", i === j);
      b.setAttribute("aria-checked", i === j ? "true" : "false");
      b.tabIndex = i === j ? 0 : -1;
    });
    if (focus) feelBtns[i]!.focus();
  };
  feelBtns.forEach((b, i) => {
    b.addEventListener("click", () => pickFeel(i));
    b.addEventListener("keydown", (e: KeyboardEvent) => {
      const step = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      pickFeel((i + step + FEELS.length) % FEELS.length, true);
    });
  });
  feelRow.append(...feelBtns);
  chipRow.append(
    ...CHIPS.map((c) => {
      const b = h("button", { type: "button", class: "pill", "aria-pressed": "false" }, CHIP_LABEL[c]);
      b.addEventListener("click", () => {
        if (chips.has(c)) chips.delete(c);
        else chips.add(c);
        b.classList.toggle("on", chips.has(c));
        b.setAttribute("aria-pressed", chips.has(c) ? "true" : "false");
      });
      return b;
    }),
  );
  const text = h("textarea", { name: "body", rows: 2, maxlength: NOTE_MAX, placeholder: "Love this bit; the sound is weird…", "aria-describedby": `nt-hint-${session.id}` }) as HTMLTextAreaElement;
  // the screenshot: a hidden file input behind a pill; once picked, a thumbnail with Remove
  const file = h("input", { type: "file", accept: "image/png,image/jpeg,image/webp", class: "sr-only", id: `nt-file-${session.id}` }) as HTMLInputElement;
  const attach = h("div", { class: "nt-attach" });
  const drawAttach = () => {
    const f = file.files?.[0];
    if (!f) return clear(attach, file, h("label", { class: "btn small ghost", for: file.id }, "Add a screenshot"));
    const url = URL.createObjectURL(f);
    clear(
      attach,
      file,
      h("img", { class: "nt-thumb", src: url, alt: "Your screenshot" }),
      h("button", { type: "button", class: "btn small ghost", onclick: () => ((file.value = ""), URL.revokeObjectURL(url), drawAttach(), file.focus()) }, "Remove"),
    );
  };
  file.addEventListener("change", drawAttach);
  drawAttach();
  // thanks by name in release notes: adults only (the server ignores it for 13-17), so a minor never sees the offer
  const credit = currentMe()?.ageBand === "18+" ? (h("input", { type: "checkbox", name: "credit" }) as HTMLInputElement) : null;
  const done = (sent: boolean) => {
    markNoted(session.id);
    onDone(sent);
  };
  const form: HTMLFormElement = h(
    "form",
    {
      class: "nt-card",
      "aria-label": `Leave ${g.title} a note`,
      onsubmit: async (e: Event) => {
        e.preventDefault();
        if (!feel) return;
        send.disabled = true;
        try {
          const shotIds: string[] = [];
          const f = file.files?.[0];
          if (f) {
            // the raw image as the body (api() sends JSON), private: attached to the note, only the developer sees it
            const r = await fetch(`${API_BASE.community}/games/${g.id}/shots?version=${encodeURIComponent(g.versionLabel ?? "")}`, {
              method: "POST",
              headers: { "content-type": f.type, authorization: `Bearer ${(await accessToken()) ?? ""}` },
              body: f,
            });
            if (!r.ok) throw new ApiError(r.status, "shot_failed", "The screenshot didn't upload (PNG, JPEG or WebP up to 5 MB). Try without it?");
            shotIds.push(((await r.json()) as { shot: Shot }).shot.id);
          }
          await api("community", `/games/${g.id}/notes`, {
            body: noteBody({ feel, chips, text: text.value, version: g.versionLabel ?? "", sessionId: session.id, seconds: session.seconds, shotIds, credit: !!credit?.checked }),
          });
          clear(
            form,
            h(
              "p",
              { class: "nt-thanks" },
              h("strong", null, claimed(g) ? "Thanks, it's with the developer." : "Thanks, it's saved for the developer."),
              claimed(g) ? " If they change something because of it, you'll hear when it ships." : " They'll read it once they claim this page, and you'll hear if a change ships because of it.",
            ),
          );
          done(true);
        } catch (err) {
          if (err instanceof ApiError && err.code === "already_noted") {
            clear(form, h("p", { class: "nt-thanks" }, "You already left a note for this session. Thanks!"));
            done(true);
            return;
          }
          send.disabled = false;
          clear(msg, errorBox(err));
        }
      },
    },
    h("div", { class: "nt-head" }, h("h3", null, `How was ${g.title}?`), h("button", { type: "button", class: "nt-skip", onclick: () => (form.remove(), done(false)) }, "Skip")),
    feelRow,
    chipRow,
    h("label", { class: "sr-only", for: `nt-text-${session.id}` }, "Your note (optional)"),
    Object.assign(text, { id: `nt-text-${session.id}` }),
    h("p", { class: "dim small", id: `nt-hint-${session.id}` }, NOTE_HINT, " ", readerLine(g)),
    h(
      "div",
      { class: "nt-extra" },
      attach,
      credit ? h("label", { class: "check small" }, credit, "Thank me by name if it ships") : null,
    ),
    h("div", { class: "nt-actions" }, send),
    msg,
  );
  return form;
}

/**
 * Opens the card when the player comes back from "Play in browser" for this game (a click on a new-tab link to its
 * URL, then the page visible again after MIN_SESSION_SECONDS). Calls `show` with the session once, and reports the
 * session's time to accounts (src/playtime.ts) whether or not a note follows.
 */
export function watchBrowserSession(g: Game, show: (s: CardSession) => void, now = () => Date.now(), alive: () => boolean = () => true, report: (s: PlaySession | null) => unknown = reportWebSession): () => void {
  let started: { at: number; id: string } | null = null;
  // the page went away (another route rendered): stop listening
  const gone = () => {
    if (alive()) return false;
    stop();
    return true;
  };
  const onClick = (e: Event) => {
    if (gone()) return;
    const a = (e.target as Element | null)?.closest?.("a[target=_blank]") as HTMLAnchorElement | null;
    if (a && a.href && g.url && a.href.replace(/\/$/, "") === g.url.replace(/\/$/, "")) {
      const at = now();
      started = { at, id: webSessionId(g.id, at) };
    }
  };
  const onVisible = () => {
    if (gone()) return;
    if (document.visibilityState !== "visible" || !started) return;
    const seconds = (now() - started.at) / 1000;
    const s = started;
    started = null;
    if (seconds < MIN_SESSION_SECONDS) return;
    if (signedIn()) void report(webSession(g.id, s.at, now(), s.id));
    if (!noted(s.id)) show({ id: s.id, seconds });
  };
  const stop = () => {
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("visibilitychange", onVisible);
  };
  document.addEventListener("click", onClick, true);
  document.addEventListener("visibilitychange", onVisible);
  return stop;
}

// ---- Media and Notes tabs on the game page ------------------------------------------------

type PlayerTab = "media" | "notes";

/**
 * The game page's players block: Media (shared screenshots and clips) and Notes (what players said → what changed,
 * and leaving a note). Starts on whichever has something; signed out with nothing to show, it stays hidden.
 */
export function playerTabs(g: Game): HTMLElement {
  ensureNotesStyle();
  const host = h("div", { class: "nt-panel", role: "tabpanel", id: `nt-panel-${g.id}` });
  const tabs = h("div", { class: "nt-tabs", role: "tablist", "aria-label": "From players" });
  const el = h("section", { class: "section nt-players", id: "players", hidden: true }, h("div", { class: "section-head" }, h("h2", { class: "nt-title" }, "From players")), tabs, host);
  const media = loadMedia(g).catch(() => ({ shots: [] as Shot[], clips: [] as Clip[] }));
  const changes = api<Changes>("community", `/games/${g.id}/changes`, { auth: false }).catch(() => null);
  // tabs are built once; switching only changes their state, so keyboard focus stays on the tab
  const LABEL: Record<PlayerTab, string> = { media: "Media", notes: "Notes" };
  let shown: PlayerTab[] = [];
  let current: PlayerTab | null = null;
  const btns = new Map<PlayerTab, HTMLButtonElement>();
  const show = (t: PlayerTab, focus = false) => {
    if (!shown.includes(t)) {
      shown = (["media", "notes"] as PlayerTab[]).filter((x) => x === t || shown.includes(x));
      drawTabs();
    }
    current = t;
    for (const [id, b] of btns) {
      b.setAttribute("aria-selected", id === t ? "true" : "false");
      b.tabIndex = id === t ? 0 : -1;
    }
    if (focus) btns.get(t)?.focus();
    host.setAttribute("aria-labelledby", `nt-tab-${g.id}-${t}`);
    clear(host, h("p", { class: "dim small" }, "Loading…"));
    void (t === "media" ? media.then((m) => current === "media" && clear(host, mediaTab(g, m))) : changes.then((c) => current === "notes" && clear(host, notesTab(g, c))));
  };
  const drawTabs = () => {
    btns.clear();
    // one tab: no tab row, the section shows its content directly
    tabs.hidden = shown.length < 2;
    clear(
      tabs,
      shown.map((id, i) => {
        const b = h("button", { type: "button", role: "tab", id: `nt-tab-${g.id}-${id}`, class: "nt-tab", "aria-controls": host.id, "aria-selected": "false", tabindex: "-1" }, LABEL[id]);
        b.addEventListener("click", () => show(id));
        b.addEventListener("keydown", (e: KeyboardEvent) => {
          const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
          if (!step) return;
          e.preventDefault();
          show(shown[(i + step + shown.length) % shown.length]!, true);
        });
        btns.set(id, b);
        return b;
      }),
    );
  };
  void Promise.all([media, changes]).then(([m, c]) => {
    const hasMedia = m.shots.length + m.clips.length > 0;
    const hasNotes = !!c && (c.versions.length > 0 || c.open_notes > 0);
    // a tab only when it has something; Notes also for a signed-in player (it's where you leave one)
    shown = [...(hasMedia ? ["media" as const] : []), ...(hasNotes || signedIn() ? ["notes" as const] : [])];
    if (!shown.length) return;
    drawTabs();
    el.hidden = false;
    show(shown[0]!);
  });
  // back from "Play in browser": the card opens on the Notes tab, once
  watchBrowserSession(g, (s) => {
    if (!signedIn()) return;
    el.hidden = false;
    show("notes");
    void changes.then(() => {
      host.prepend(noteCard(g, s, () => {}));
      host.scrollIntoView({ block: "nearest", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
    });
  }, undefined, () => el.isConnected);
  return el;
}

async function loadMedia(g: Game): Promise<{ shots: Shot[]; clips: Clip[] }> {
  const [shots, clips] = await Promise.all([
    api<{ shots: Shot[] }>("community", `/games/${g.id}/shots?limit=24`).then((r) => r.shots, () => [] as Shot[]),
    api<{ clips: Clip[] }>("community", `/games/${g.id}/clips?limit=12`).then((r) => r.clips.filter((c) => c.status === "visible"), () => [] as Clip[]),
  ]);
  // only http(s) links reach a src (clips.ts safeMediaUrl)
  return { shots: shots.flatMap((s) => (safeMediaUrl(s.file_url) ? [{ ...s, file_url: safeMediaUrl(s.file_url) }] : [])), clips };
}

export function mediaTab(g: Game, m: { shots: Shot[]; clips: Clip[] }): HTMLElement {
  return h(
    "div",
    null,
    h(
      "ul",
      { class: "nt-media", "aria-label": `Player media for ${g.title}` },
      m.clips.map((c) =>
        h(
          "li",
          { class: "nt-tile nt-clip" },
          link(clipPath(c.id), { "aria-label": `Clip: ${c.title ?? g.title}` }, safeMediaUrl(c.thumbnail_url) ? h("img", { src: safeMediaUrl(c.thumbnail_url)!, alt: "", loading: "lazy" }) : h("span", { class: "nt-ph" }, "▶"), h("span", { class: "nt-cap" }, h("span", { class: "nt-kind" }, "Clip"), c.title ?? "Clip")),
        ),
      ),
      m.shots.map((s, i) =>
        h(
          "li",
          { class: "nt-tile" },
          h("a", { href: s.file_url!, target: "_blank", rel: "noopener" }, h("img", { src: s.file_url!, alt: `Player screenshot ${i + 1}${s.version ? ` (${vLabel(s.version)})` : ""}`, loading: "lazy" }), h("span", { class: "nt-cap" }, fmtAgo(s.shared_at ?? s.created_at))),
        ),
      ),
    ),
  );
}

export function notesTab(g: Game, c: Changes | null): HTMLElement {
  const versions = c?.versions ?? [];
  return h(
    "div",
    { class: "nt-notes" },
    h(
      "p",
      { class: "nt-lede" },
      "Players leave quick notes after they play. ",
      h(
        "span",
        { class: "dim" },
        claimed(g)
          ? c?.open_notes
            ? `${c.open_notes} open with the developer right now.`
            : "Only the developer reads them."
          : c?.open_notes
            ? `${c.open_notes} waiting for the developer to claim this page.`
            : "They wait here until the developer claims this page.",
      ),
    ),
    signedIn() ? leaveNote(g) : h("p", { class: "small" }, link(`/signin?next=/g/${g.id}%23players`, null, "Sign in"), " to leave a note after you play."),
    versions.length
      ? h(
          "div",
          null,
          h("h3", { class: "nt-sub" }, "Changed because of players' notes"),
          h(
            "ol",
            { class: "nt-changes" },
            versions.map((v) =>
              h(
                "li",
                { class: "nt-change" },
                h("div", { class: "nt-ver" }, h("strong", null, vLabel(v.version)), h("span", { class: "dim small" }, ` ${fmtAgo(v.live_at)}`)),
                h(
                  "div",
                  null,
                  h("p", { class: "dim small" }, answeredLine(v)),
                  v.changes.length ? h("ul", { class: "nt-lines" }, v.changes.map((x) => h("li", null, x.change, x.notes > 1 ? h("span", { class: "dim small" }, ` · ${x.notes} notes`) : null))) : null,
                  v.thanks.length ? thanksLine(v.thanks) : null,
                ),
              ),
            ),
          ),
        )
      : null,
  );
}

function thanksLine(ids: string[]): HTMLElement {
  const p = h("p", { class: "small nt-thanks-line" }, "Thanks to ");
  ids.slice(0, 12).forEach((id, i) => p.append(i ? ", " : "", nameSpan(id)));
  if (ids.length > 12) p.append(` and ${ids.length - 12} more`);
  return p;
}

/** Signed in, on the Notes tab: a note any time (the session is this visit), folded until asked for. */
function leaveNote(g: Game): HTMLElement {
  const d = h("details", { class: "nt-leave" }, h("summary", { class: "btn small ghost" }, "Leave a note"));
  const session = { id: `web-${g.id}-page`, seconds: 0 };
  // sent: the thanks stays in view; skipped: the fold closes, with a fresh card behind it
  const add = () =>
    d.append(
      noteCard(g, { ...session, id: `${session.id}-${Date.now().toString(36)}` }, (sent) => {
        if (sent) return;
        d.open = false;
        add();
        (d.querySelector("summary") as HTMLElement | null)?.focus();
      }),
    );
  add();
  return d;
}

// ---- Style ------------------------------------------------------------------------------

const CSS = `
.nt-players .section-head h2 { position: relative; padding-left: 14px; }
.nt-players .section-head h2::before { content: ""; position: absolute; left: 0; top: .2em; bottom: .2em; width: 4px; border-radius: 2px; background: var(--gt-accent, var(--ruby)); }
.nt-tabs { display: flex; gap: 24px; margin: 0 0 16px; border-bottom: 1px solid var(--border); }
.nt-tab { position: relative; min-height: 40px; padding: 0 2px; background: none; border: 0; color: var(--dim); font: inherit; font-weight: 600; cursor: pointer; }
.nt-tab:hover { color: var(--text); }
.nt-tab[aria-selected="true"] { color: var(--text); }
.nt-tab[aria-selected="true"]::after { content: ""; position: absolute; left: 0; right: 0; bottom: -1px; height: 2px; background: var(--gt-accent, var(--ruby)); border-radius: 2px; }
.nt-tab:focus-visible { outline: 2px solid var(--gt-accent, var(--ruby)); outline-offset: 2px; }
.nt-media { list-style: none; margin: 0; padding: 0; display: grid; gap: 10px; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); }
.nt-tile a { position: relative; display: block; aspect-ratio: 16 / 9; border-radius: 12px; overflow: hidden; background: var(--card); color: var(--text); text-decoration: none; }
.nt-tile img { width: 100%; height: 100%; object-fit: cover; display: block; }
.nt-ph { display: grid; place-items: center; height: 100%; font-size: 1.6rem; color: var(--dim); }
.nt-cap { position: absolute; left: 0; right: 0; bottom: 0; padding: 18px 10px 8px; font-size: .8rem; background: linear-gradient(transparent, rgba(0,0,0,.75)); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.nt-kind { display: inline-block; margin-right: 6px; padding: 0 6px; border-radius: 6px; background: rgba(255,255,255,.18); font-size: .72rem; }
.nt-empty { padding: 20px; border: 1px dashed var(--border); border-radius: 12px; }
.nt-empty p { margin: 0 0 4px; }
.nt-lede { margin: 0 0 12px; }
.nt-sub { margin: 24px 0 10px; font-size: 1rem; }
.nt-changes { list-style: none; margin: 0; padding: 0; display: grid; gap: 12px; }
.nt-change { display: grid; grid-template-columns: 88px minmax(0, 1fr); gap: 12px; padding: 14px 16px; border-radius: 12px; background: var(--panel); border: 1px solid var(--border); }
.nt-change p { margin: 0 0 6px; }
.nt-ver { display: flex; flex-direction: column; }
.nt-lines { margin: 0; padding-left: 18px; }
.nt-lines li { margin: 2px 0; }
.nt-leave > summary { list-style: none; display: inline-flex; align-items: center; cursor: pointer; }
.nt-leave > summary::-webkit-details-marker { display: none; }
.nt-leave[open] > summary { display: none; }
.nt-card { display: grid; gap: 12px; padding: 18px; margin: 4px 0 8px; border-radius: 16px; background: var(--panel); border: 1px solid var(--border); max-width: 640px; }
.nt-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.nt-head h3 { margin: 0; font-size: 1.1rem; }
.nt-skip { background: none; border: 0; color: var(--dim); font: inherit; cursor: pointer; min-height: 36px; padding: 0 6px; }
.nt-skip:hover { color: var(--text); }
.nt-feel { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
.nt-feel-btn { display: flex; flex-direction: column; align-items: center; gap: 2px; min-height: 64px; padding: 8px 4px; border-radius: 12px; border: 1px solid var(--border); background: var(--card); color: var(--text); font: inherit; font-size: .9rem; cursor: pointer; }
.nt-feel-btn:hover { background: var(--hover); }
.nt-feel-btn.on { border-color: var(--ruby); background: rgba(255,90,122,.14); }
.nt-dot { display: inline-block; width: 12px; height: 12px; border-radius: 50%; margin: 4px; flex: none; }
.nt-dot-love { background: var(--ruby); } .nt-dot-good { background: var(--ok); } .nt-dot-meh { background: #e6b85c; } .nt-dot-rough { background: var(--faint); }
.nt-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.nt-card textarea { width: 100%; padding: 10px 12px; border-radius: 10px; border: 1px solid var(--border); background: var(--card); color: var(--text); font: inherit; resize: vertical; }
.nt-card .dim.small { margin: -6px 0 0; }
.nt-extra { display: flex; flex-wrap: wrap; gap: 8px 16px; align-items: center; }
.nt-attach { display: inline-flex; align-items: center; gap: 8px; }
.nt-attach label.btn { cursor: pointer; }
.nt-attach input:focus-visible + label { outline: 2px solid var(--ruby); outline-offset: 2px; }
.nt-thumb { width: 96px; aspect-ratio: 16 / 9; object-fit: cover; border-radius: 8px; display: block; }
.nt-actions { display: flex; gap: 8px; }
.nt-thanks { margin: 0; }
@media (max-width: 600px) {
  .nt-change { grid-template-columns: 1fr; gap: 4px; }
  .nt-ver { flex-direction: row; gap: 8px; align-items: baseline; }
  .nt-media { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .nt-card { padding: 14px; }
  .nt-feel-btn { min-height: 56px; font-size: .82rem; }
}
/* the creator inbox */
.nti-bar { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin: 8px 0 16px; }
.nti-bar select { min-height: 36px; border-radius: 10px; border: 1px solid var(--border); background: var(--panel); color: var(--text); font: inherit; padding: 0 10px; }
.nti-groups { display: flex; flex-wrap: wrap; gap: 8px; margin: 0 0 16px; padding: 0; list-style: none; }
.nti-groups button { display: inline-flex; gap: 6px; align-items: center; }
.nti-groups b { font-weight: 600; color: var(--text); }
.nti-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 10px; }
.nti-note { padding: 14px 16px; border-radius: 12px; background: var(--panel); border: 1px solid var(--border); display: grid; gap: 8px; }
.nti-note.folded { margin-left: 28px; opacity: .85; }
.nti-top { display: flex; flex-wrap: wrap; gap: 6px 10px; align-items: center; }
.nti-feel { font-weight: 600; display: inline-flex; align-items: center; } .nti-feel .nt-dot { margin: 0 6px 0 0; width: 10px; height: 10px; }
.nti-tag { padding: 1px 8px; border-radius: 999px; background: var(--card); font-size: .78rem; color: var(--dim); }
.nti-tag.st-addressed { color: var(--ok); }
.nti-metoo { color: var(--ruby); font-size: .85rem; font-weight: 600; }
.nti-body { margin: 0; font-size: 1rem; }
.nti-env { margin: 0; }
.nti-shots { display: flex; gap: 8px; flex-wrap: wrap; }
.nti-shots img { width: 160px; aspect-ratio: 16 / 9; object-fit: cover; border-radius: 8px; display: block; }
.nti-act { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.nti-act input { min-height: 36px; border-radius: 10px; border: 1px solid var(--border); background: var(--card); color: var(--text); font: inherit; padding: 0 10px; }
.nti-act input[name=version] { width: 7em; }
.nti-act input[name=change] { flex: 1 1 240px; min-width: 0; }
.nti-agent { padding: 14px 16px; border-radius: 12px; border: 1px solid var(--border); margin: 0 0 16px; display: grid; gap: 8px; }
.nti-agent > summary { cursor: pointer; list-style-position: inside; }
.nti-copy { background: none; border: 0; padding: 0; color: var(--dim); font: inherit; text-decoration: underline; text-underline-offset: 2px; cursor: pointer; }
.nti-copy:hover { color: var(--text); }
.nti-copy:focus-visible { outline: 2px solid var(--ruby); outline-offset: 2px; border-radius: 4px; }
.nti-agent code { font-size: .85rem; overflow-wrap: anywhere; padding: 6px 10px; border-radius: 8px; background: var(--card); }
@media (max-width: 600px) { .nti-note.folded { margin-left: 12px; } .nti-shots img { width: 120px; } }
`;

export function ensureNotesStyle(): void {
  if (typeof document === "undefined" || document.getElementById("nt-style")) return;
  document.head.append(h("style", { id: "nt-style" }, CSS));
}
